import React, { useContext, useState } from 'react';
import fallbackLogo from '../../../../../assets/images/DFC.svg';
import { CheckoutContext, CheckoutContextProps } from '../../../../../context/CheckoutContext';
import useDfcSurface from '../../../../../hooks/useDfcSurface';
import useFitnessVillageCopy from '../../../../../hooks/useFitnessVillageCopy';
import {
  DFC_HEADER_LOGO_DARK,
  DFC_HEADER_LOGO_WORKS_ON_DARK,
} from '../../../../../Utils/dfcArtwork';
import { CLUB15_ART, isClub15Company } from '../../../../../Utils/club15Brand';

/**
 * True when dark mode needs no white plate behind the logo: DFC's dark-background logo is set,
 * or the admin panel's logo already reads on black (`DFC_HEADER_LOGO_WORKS_ON_DARK`).
 */
export const useHasDarkHeaderLogo = (): boolean => {
  const surface = useDfcSurface();
  return surface.dark && (Boolean(DFC_HEADER_LOGO_DARK) || DFC_HEADER_LOGO_WORKS_ON_DARK);
};

/**
 * The header logo: the company's logo from the admin panel (`company.logo`), the same field every
 * other header theme reads. In dark mode, DFC's dark-background logo replaces it once supplied
 * (`DFC_HEADER_LOGO_DARK`, DFC 12/10). The bundled DFC mark stands in when the field is empty or
 * the image fails to load, so the header never shows a broken image. Its size comes from
 * `LogoPlate`.
 */
const VillageLogo: React.FC = () => {
  const { company } = useContext(CheckoutContext) as CheckoutContextProps;
  const [failedSrc, setFailedSrc] = useState<string>();
  const copy = useFitnessVillageCopy().header;
  const surface = useDfcSurface();
  // Club 15: its bundled logo for the mode (charcoal on cream, cream on charcoal).
  const logo = isClub15Company(company)
    ? surface.dark
      ? CLUB15_ART.logoCream
      : CLUB15_ART.logoCharcoal
    : surface.dark && DFC_HEADER_LOGO_DARK
      ? DFC_HEADER_LOGO_DARK
      : company?.logo;
  const src = logo && logo !== failedSrc ? logo : fallbackLogo;

  return <img src={src} alt={company?.name || copy.logoAlt} onError={() => setFailedSrc(logo)} />;
};

export default VillageLogo;
